// Inline progress + ETA shown while a LightRAG workspace is still indexing.
// Uniform across pipeline- and docset-owned KGs because both ultimately
// render the same `IDocInfo[]` from `kg.listDocs()`.
//
// Progress and ETA are chunk-weighted (chars → estimated chunks), not
// doc-count-based — a single 694K-char doc dominates wall time vs eight
// small artifacts. When LightRAG exposes workspace batch progress
// (`cur_batch` / `total_batches`), we use it for early ETA as soon as the
// first extraction batch starts — no need to wait for three docs to finish.
import { useEffect, useMemo, useRef, useState } from 'react';

import { formatDuration } from '../../lib/format';
import {
  applyEtaSmoothing,
  computeKgIndexingEta,
  type IEtaSmoothingState,
} from '../../lib/kg-indexing-eta';
import type { IDocInfo, IKgWorkspaceProgress } from '../../lib/metaphysics-kg';

import './knowledge-graphs.css';

export interface IKgIndexingProgressProps {
  docs: IDocInfo[];
  indexingCount: number;
  failedCount: number;
  workspaceProgress?: IKgWorkspaceProgress | null;
}

export function KgIndexingProgress({
  docs,
  indexingCount,
  failedCount,
  workspaceProgress = null,
}: IKgIndexingProgressProps) {
  const [nowMs, setNowMs] = useState(() => Date.now());
  const etaSmoothRef = useRef<IEtaSmoothingState | null>(null);

  useEffect(() => {
    if (indexingCount === 0) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [indexingCount]);

  useEffect(() => {
    if (indexingCount === 0) etaSmoothRef.current = null;
  }, [indexingCount]);

  const earliestUpdatedMs = useMemo(() => {
    let min: number | null = null;
    for (const d of docs) {
      if (!d.updatedAt) continue;
      const t = new Date(d.updatedAt).getTime();
      if (Number.isFinite(t) && (min === null || t < min)) min = t;
    }
    return min;
  }, [docs]);

  if (indexingCount === 0) return null;

  const jobStartMs = workspaceProgress?.jobStart
    ? new Date(workspaceProgress.jobStart).getTime()
    : NaN;
  const elapsedMs = Number.isFinite(jobStartMs)
    ? Math.max(0, nowMs - jobStartMs)
    : earliestUpdatedMs !== null
      ? Math.max(0, nowMs - earliestUpdatedMs)
      : null;

  const eta = computeKgIndexingEta({
    docs,
    elapsedMs,
    nowMs,
    inFlight: workspaceProgress
      ? {
          curBatch: workspaceProgress.curBatch,
          totalBatches: workspaceProgress.totalBatches,
          jobStart: workspaceProgress.jobStart,
        }
      : null,
  });

  const smoothed = applyEtaSmoothing({
    etaMs: eta.etaMs,
    etaReady: eta.etaReady,
    completedUnits: eta.completedUnits,
    totalUnits: eta.totalUnits,
    elapsedMs,
    nowMs,
    state: etaSmoothRef.current,
  });
  etaSmoothRef.current = smoothed.state;

  const completedDocs = eta.completedDocs;
  const totalDocs = eta.totalDocs;

  return (
    <div className="kg-indexing-row kg-indexing-row--withbar">
      <div className="kg-indexing-row__head">
        <span className="kg-spinner-sm" style={{ borderTopColor: '#acc3b3' }} />
        <span>
          Indexing · {completedDocs}/{totalDocs} docs · {eta.pctWorkload}% workload
          {failedCount > 0 ? ` · ${failedCount} failed` : ''}
        </span>
      </div>
      <progress
        className="kg-indexing-row__bar"
        value={eta.completedUnits}
        max={Math.max(1, eta.totalUnits)}
      />
      <div className="kg-indexing-row__meta">
        {elapsedMs !== null ? (
          <>
            elapsed <strong>{formatDuration(elapsedMs)}</strong>
          </>
        ) : (
          <span style={{ opacity: 0.7 }}>elapsed —</span>
        )}
        {smoothed.etaReady && smoothed.etaMs != null ? (
          <>
            {' · '}ETA <strong>~{formatDuration(smoothed.etaMs)}</strong>
          </>
        ) : elapsedMs !== null ? (
          <span style={{ opacity: 0.7 }}> · ETA estimating…</span>
        ) : null}
      </div>
    </div>
  );
}
